import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { FINANCE_TYPE, QUICKBOOKS_TYPE, SHOPIFY_AMAZON_INVENTORY } from '../consts/cronTypes';

const initialState = {
  data: {
    [FINANCE_TYPE]: null,
    [QUICKBOOKS_TYPE]: null,
    [SHOPIFY_AMAZON_INVENTORY]: null
  },
  cronInformation: {
    defaults: [],
    reports: []
  },
  loading: false,
  error: null,
}

const asyncGetCron = async (type, rejectWithValue) => {
  try {
    const { data } = await axios.get(`/api/v1/cron-status`, { params: { 
        type, 
    }});
    return data;
  } catch (error) {
    if ('string' === typeof error.response.data) {
      return rejectWithValue({ error: error.response.data })
    } else {
      return rejectWithValue({ error: error.response.data.errors })
    }
  }
}

export const getCronFinanceStatus = createAsyncThunk(
  'cron/getCronFinanceUpdate',
  async ({
    type
  }, { rejectWithValue }) => {
    return await asyncGetCron(type, rejectWithValue);
  }
)

export const getCronShopifyAmazonSalesStatus = createAsyncThunk(
  'cron/getCronShopifyAmazonSalesStatus',
  async ({
    type
  }, { rejectWithValue }) => {
    return await asyncGetCron(type, rejectWithValue);
  }
)

export const getCronQuickBooksStatus = createAsyncThunk(
  'cron/getCronQuickBooksStatus',
  async ({
    type
  }, { rejectWithValue }) => {
    return await asyncGetCron(type, rejectWithValue);
  }
)

export const getCronInformation = createAsyncThunk(
  'cron/getCronInformation',
  async (_options = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/cron/information`);
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const updateCronInformation = createAsyncThunk(
  'cron/updateCronInformation',
  async ({
    singleData
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/cron/update-information`, { singleData });
      dispatch(getCronInformation({}));
      await axios.post(`/api/v1/cron/update-cron-schedules`, { type: singleData.type })
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const setCronPaused = createAsyncThunk(
  'cron/setCronPaused',
  async ({ cron, isPaused }, { dispatch }) => {
    return await dispatch(updateCronInformation({
      singleData: { ...cron, isPaused }
    })).unwrap();
  }
)

export const cronSlice = createSlice({
  name: 'cron',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCronShopifyAmazonSalesStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCronShopifyAmazonSalesStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.data[SHOPIFY_AMAZON_INVENTORY] = action.payload;
      })
      .addCase(getCronShopifyAmazonSalesStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "";
        state.data[SHOPIFY_AMAZON_INVENTORY] = null;
      })
      .addCase(getCronFinanceStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCronFinanceStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.data[FINANCE_TYPE] = action.payload;
      })
      .addCase(getCronFinanceStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "";
        state.data[FINANCE_TYPE] = null;
      })
      .addCase(getCronQuickBooksStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCronQuickBooksStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.data[QUICKBOOKS_TYPE] = action.payload;
      })
      .addCase(getCronQuickBooksStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "";
        state.data[QUICKBOOKS_TYPE] = null;
      })
      .addCase(getCronInformation.pending, (state, action) => {
        if (action.meta.arg?.silent) return;
        state.loading = true;
        state.error = null;
      })
      .addCase(getCronInformation.fulfilled, (state, action) => {
        state.loading = false;
        state.cronInformation = {
          defaults: action.payload?.defaults || [],
          reports: action.payload?.reports || []
        };
      })
      .addCase(getCronInformation.rejected, (state, action) => {
        if (action.meta.arg?.silent) return;
        state.loading = false;
        state.error = action.payload?.error || "";
        state.cronInformation = {
          defaults: [],
          reports: []
        };
      })
      .addCase(updateCronInformation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCronInformation.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateCronInformation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "";
      })
  }
})

export const {} = cronSlice.actions;

export default cronSlice.reducer
